import { unwrapResult } from "@reduxjs/toolkit";
import { Checkbox, Form, Input, Modal, ModalProps, notification, Radio } from "antd";
import React, { ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { RootDispatch } from "../../app/store";
import {
  DepartmentCategories,
  DepartmentCategory,
  DepartmentSubCategories,
  getDepartmentCategoryName,
  getDepartmentSubCategories,
  getDepartmentSubCategoryName,
} from "../../models/department";
import {
  createOrUpdateDepartment,
  getDepartmentDetail,
} from "./departmentSlice";
import styles from "./Modal.module.css";

interface DepartmentModalProps {
  departmentId?: string;
}
export const DepartmentModal = ({
  departmentId,
  onCancel,
  onOk,
  ...others
}: DepartmentModalProps & ModalProps): ReactElement => {
  const dispatch = useDispatch<RootDispatch>();

  const { tenantId } = useParams<{ tenantId: string | undefined }>();

  const [form] = Form.useForm();

  const [isSubcategoriesEditable, setIsSubCategoriesEditable] = useState(true);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (departmentId) {
      dispatch(getDepartmentDetail({ departmentId, tenantId }))
        .then(unwrapResult)
        .then((d) => {
          form.setFieldsValue(d);
          setIsSubCategoriesEditable(
            getDepartmentSubCategories(d.category).editable
          );
        });
    }
  }, [dispatch, departmentId]);

  return (
    <Modal
      {...others}
      title={`${departmentId ? "编辑" : "创建"}部门科室`}
      okText="确定"
      cancelText="取消"
      confirmLoading={isLoading}
      onCancel={(e) => {
        if (departmentId) {
          form.resetFields();
          setIsSubCategoriesEditable(true);
        }
        onCancel && onCancel(e);
      }}
      onOk={(e) => {
        form
          .validateFields()
          .then((values) => {
            dispatch(
              createOrUpdateDepartment({
                department: { ...values, id: departmentId },
                tenantId,
              })
            )
              .then((res: any) => {
                if (res.error.name == 'Error') {
                  setIsLoading(false)
                  return
                }
              })
              .catch(() => {
                setIsLoading(false);
                form.resetFields();
                setIsSubCategoriesEditable(true);
                onOk && onOk(e);
                notification.success({
                  message: "新增成功",
                  duration: 3,
                })
                // setIsLoading(false);
              });
          })
          .catch(() => {
            // do nothing
          });
      }}
    >
      <Form
        form={form}
        colon={false}
        autoComplete="off"
        initialValues={{
          category: DepartmentCategory.General,
          subCategory: getDepartmentSubCategories(DepartmentCategory.General)
            .categories,
        }}
      >
        <Form.Item
          name="code"
          label="科室代码"
          rules={[{ required: true, message: "科室代码不能为空" },{max:40,message:"最长40位!"}]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="name"
          label="科室名称"
          rules={[{ required: true, message: "科室名称不能为空" },{max:60,message:"最长60位!"}]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="科室类型" className={styles.categories}>
          <Form.Item name="category">
            <Radio.Group
              className={styles.column}
              onChange={(e) => {
                const subs = getDepartmentSubCategories(e.target.value);
                setIsSubCategoriesEditable(subs.editable);
                form.setFieldsValue({ subCategory: subs.categories });
              }}
            >
              {DepartmentCategories.map((c) => {
                const name = getDepartmentCategoryName(c);
                return (
                  <Radio key={name} value={c}>
                    {name}
                  </Radio>
                );
              })}
            </Radio.Group>
          </Form.Item>
          <Form.Item name="subCategory">
            <Checkbox.Group
              className={styles.column}
              disabled={!isSubcategoriesEditable}
            >
              {DepartmentSubCategories.map((c) => {
                const name = getDepartmentSubCategoryName(c);
                return (
                  <Checkbox key={c} value={c}>
                    {name}
                  </Checkbox>
                );
              })}
            </Checkbox.Group>
          </Form.Item>
        </Form.Item>
      </Form>
    </Modal>
  );
};
